'use client';

import { useId, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faArrowLeft, faCirclePlus, faFilter } from '@icons/classic/regular';
import { Button, Checkbox, FlexContainer, Popover, PopoverContent, PopoverTrigger, RadioGroupList } from '../components';
import { Body, Label } from '../typography';

import type { FilterOption } from '@bridge/types';

export type AddFilterOption = FilterOption<string>;

export interface AddFilterCategory {
  key: string;
  label: string;
  title: string;
  options: readonly AddFilterOption[];
  selectedValues: readonly string[];
  onChange: (values: string[]) => void;
  /** When `single`, options are mutually exclusive (radio). Defaults to `multiple` (checkbox). */
  selectionMode?: 'single' | 'multiple';
}

export interface AddFilterPopoverProps {
  categories: readonly AddFilterCategory[];
  onCategoryOpen?: (key: string) => void;
  align?: React.ComponentProps<typeof PopoverContent>['align'];
  label?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function AddFilterPopover({
  categories,
  onCategoryOpen,
  align = 'start',
  label = 'Add filter',
  className,
  size = 'md',
}: AddFilterPopoverProps) {
  const checkboxIdPrefix = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategoryKey, setActiveCategoryKey] = useState<string | null>(null);
  const activeCategory = categories.find((category) => category.key === activeCategoryKey);

  function handleOpenChange(open: boolean) {
    setIsOpen(open);
    if (!open) {
      setActiveCategoryKey(null);
    }
  }

  function handleCategoryOpen(category: AddFilterCategory) {
    setActiveCategoryKey(category.key);
    onCategoryOpen?.(category.key);
  }

  function handleOptionChange(category: AddFilterCategory, value: string) {
    if (category.selectionMode === 'single') {
      category.onChange([value]);
      return;
    }

    const nextValues = category.selectedValues.includes(value)
      ? category.selectedValues.filter((selectedValue) => selectedValue !== value)
      : [...category.selectedValues, value];
    category.onChange(nextValues);
  }

  const allOptionValue = '__all__';

  function renderCategoryOptions(category: AddFilterCategory) {
    if (category.selectionMode === 'single') {
      const selectedValue = category.selectedValues[0] ?? allOptionValue;

      return (
        <RadioGroupList
          labelSize="sm"
          onValueChange={(value) => {
            if (value === allOptionValue) {
              category.onChange([]);
              return;
            }

            category.onChange([value]);
          }}
          options={[
            { label: 'All', value: allOptionValue },
            ...category.options.map((option) => ({ label: option.label, value: option.value })),
          ]}
          value={selectedValue}
        />
      );
    }

    return (
      <>
        <FlexContainer className="w-full gap-2 px-3 py-2" items="center">
          <Checkbox
            checked={category.selectedValues.length === 0}
            id={`${checkboxIdPrefix}-all`}
            onCheckedChange={() => category.onChange([])}
          />
          <Label className="cursor-pointer text-content-primary" htmlFor={`${checkboxIdPrefix}-all`}>
            All
          </Label>
        </FlexContainer>
        {category.options.map((option, index) => {
          const checkboxId = `${checkboxIdPrefix}-${category.key}-${index}`;
          return (
            <FlexContainer className="w-full gap-2 px-3 py-2" items="center" key={option.value}>
              <Checkbox
                checked={category.selectedValues.includes(option.value)}
                id={checkboxId}
                onCheckedChange={() => handleOptionChange(category, option.value)}
              />
              <Label className="cursor-pointer text-content-primary" htmlFor={checkboxId}>
                {option.label}
              </Label>
            </FlexContainer>
          );
        })}
      </>
    );
  }

  return (
    <Popover onOpenChange={handleOpenChange} open={isOpen}>
      <PopoverTrigger asChild>
        <Button className={className} size={size} variant="tertiary">
          <FontAwesomeIcon icon={faFilter} />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 rounded-sm border-border-secondary bg-background-primary p-0 shadow-md"
        align={align}
      >
        {activeCategory ? (
          <FlexContainer className="w-full py-1.5" direction="column">
            <FlexContainer className="w-full gap-2 border-b border-border-secondary px-3 py-2" items="center">
              <button
                className="cursor-pointer p-1 text-content-secondary"
                aria-label="Back to filter categories"
                onClick={() => setActiveCategoryKey(null)}
                type="button"
              >
                <FontAwesomeIcon className="size-3.5" icon={faArrowLeft} />
              </button>
              <Body className="text-content-secondary" weight="medium">
                {activeCategory.title}
              </Body>
            </FlexContainer>
            <FlexContainer className="w-full" direction="column">
              <FlexContainer className="w-full flex-col gap-1 px-3 py-2">
                {renderCategoryOptions(activeCategory)}
              </FlexContainer>
            </FlexContainer>
          </FlexContainer>
        ) : (
          <FlexContainer direction="column">
            {categories.map((category) => (
              <button
                className="w-full cursor-pointer px-3 py-2 text-left first:rounded-t-sm last:rounded-b-sm hover:bg-background-secondary"
                key={category.key}
                onClick={() => handleCategoryOpen(category)}
                type="button"
              >
                <FlexContainer className="gap-2" items="center">
                  <FontAwesomeIcon className="size-4 text-content-secondary" icon={faCirclePlus} />
                  <Body className="text-content-primary">{category.label}</Body>
                </FlexContainer>
              </button>
            ))}
          </FlexContainer>
        )}
      </PopoverContent>
    </Popover>
  );
}
